import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { padding, transparentize } from "polished";
import React from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import { Link } from "@brexhq/next/src/components/shared/Link";
import { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";

import type { INavGroup } from "../../types";

import { NavColumn } from "./NavColumn";

const Title = styled.h2(({ theme }) => ({
  ...theme.typography.BodySmallSB,
  margin: 0,
}));

const OuterContainer = styled(Container, {
  shouldForwardProp: (prop) => !["collapsePaddingRight", "span"].includes(prop),
})<{
  collapsePaddingRight?: boolean;
  collapsePaddingLeft?: boolean;
  span: number;
}>(({ theme, collapsePaddingRight, collapsePaddingLeft, span }) => ({
  gridColumn: `span ${span}`,
  ...padding(
    theme.spacing.LRG.med,
    collapsePaddingRight ? theme.spacing.LRG.med / 2 : theme.spacing.LRG.med,
    theme.spacing.LRG.lrg,
    collapsePaddingLeft ? theme.spacing.LRG.med / 2 : theme.spacing.LRG.med,
  ),
}));

const InnerContainer = styled(Container)(({ theme }) => ({
  borderBottom: `1px solid ${transparentize(
    0.8,
    theme.colors.content.tertiary,
  )}`,
}));

const ModuleContainer = styled.div<{ span: number }>(({ theme, span }) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${span}, 1fr)`,
  gap: theme.spacing.LRG.lrg,
  [theme.breakpoints.nav.MED]: {
    gap: theme.spacing.MED.med,
  },
}));

interface Props {
  group: INavGroup;
  collapsePaddingRight?: boolean;
  collapsePaddingLeft?: boolean;
  darkModeNav?: boolean;
}

/**
 * Used to override the background color of the nav group if darkModeNav is enabled.
 */
function useNavGroupBackgroundColor(
  navGroupTheme: ModuleThemeType,
  darkModeNav?: boolean,
) {
  const theme = useTheme();
  const {
    standard: { background },
  } = getModuleThemeColors(navGroupTheme);

  if (darkModeNav) {
    if (navGroupTheme === "PrimaryWhite") {
      return theme.colors.background.primary;
    }
    if (navGroupTheme === "PrimaryGray") {
      return theme.colors.background.secondary;
    }
  }

  return background;
}

export const NavGroup = ({
  group: { title, span, theme: navGroupTheme, columns },
  collapsePaddingRight,
  collapsePaddingLeft,
  darkModeNav,
}: Props) => {
  const background = useNavGroupBackgroundColor(navGroupTheme, darkModeNav);

  return (
    <OuterContainer
      display="flex"
      backgroundColor={background}
      direction="column"
      gap="sml"
      span={span}
      collapsePaddingRight={collapsePaddingRight}
      collapsePaddingLeft={collapsePaddingLeft}
    >
      {!title.hideOnDesktop && (
        <InnerContainer
          display="flex"
          justifyContent="space-between"
          padding={["none", "none", "xxsml", "none"]}
        >
          <Title as="h2">{title.text}</Title>
          {title.link?.text && (
            <Link
              to={
                title.link.link.to ||
                title.link.link.toExternal ||
                title.link.link.hash ||
                "#"
              }
              noFollow={title.link.link.noFollow}
              label={title.link.text}
              modifier="arrow"
              component="navGroup link"
              // remove this from tab order since this will be controlled
              // exclusively by arrow keys.
              tabIndex={-1}
              size={{
                LRG: "DEVIATED_NAV_SIZE",
                MED: "DEVIATED_NAV_SIZE",
                SML: "DEVIATED_NAV_SIZE",
              }}
            />
          )}
        </InnerContainer>
      )}
      <ModuleContainer span={span}>
        {(columns || []).map((column) => (
          <NavColumn key={column._key} column={column} />
        ))}
      </ModuleContainer>
    </OuterContainer>
  );
};
